// src/pages/purchasedetails/PurchaseDetailsId.tsx

import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { IoIosArrowBack } from 'react-icons/io';
import { FaDownload } from 'react-icons/fa6';
import Cookies from "js-cookie";
import { PaymentDTO } from "../../../types/types";
import { instance } from "../../../api/axiosInstance";
import { formatNumber } from "../../../utils/formatNumber";

interface LocationState {
  payment: PaymentDTO;
}

export default function PurchaseDetailsId() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // state에서 payment 데이터 추출
  const state = location.state as LocationState | undefined;
  const initialPayment = state?.payment;

  const [payment, setPayment] = useState<PaymentDTO | null>(initialPayment || null);
  const [loading, setLoading] = useState<boolean>(!initialPayment);
  const [error, setError] = useState<string | null>(null);
  const [isDetailVisible, setIsDetailVisible] = useState<boolean>(false); // 상세 정보 표시 상태

  useEffect(() => {
    if (!initialPayment && id) {
      // state에 payment 데이터가 없을 경우, ID를 통해 데이터 fetching
      const fetchPayment = async () => {
        const accessToken = Cookies.get('access_token');

        if (!accessToken) {
          setError('인증 토큰이 없습니다. 로그인해주세요.');
          setLoading(false);
          return;
        }

        try {
          const response = await instance.get<PaymentDTO>(`/auth/payments/${id}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          });
          setPayment(response.data);
        } catch (err) {
          console.error('Error fetching payment details:', err);
          setError('결제 상세 정보를 가져오는 중 오류가 발생했습니다.');
        } finally {
          setLoading(false);
        }
      };

      fetchPayment();
    }
  }, [initialPayment, id]);

  // 할인율과 할인 금액 계산
  const discountRate = Number(payment?.product?.discount) || 0;
  const discountAmount = (payment?.product?.price || 0) * discountRate / 100;
  const totalPrice = (payment?.product?.price || 0) - discountAmount;

  // 다운로드 핸들러
  const handleDownload = async () => {
    if (!payment?.product?.id || !payment.product.name) {
      console.error('Product information is incomplete.');
      alert('다운로드할 상품 정보가 없습니다.');
      return;
    }

    const accessToken = Cookies.get('access_token');

    if (!accessToken) {
      console.error('Authentication token is missing.');
      alert('인증 토큰이 없습니다. 로그인해주세요.');
      return;
    }

    try {
      const response = await instance.get('/auth/files/download', {
        params: { productId: payment.product.id },
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        responseType: 'blob',
      });

      let filename = `${payment.product.name}.zip`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // 정리
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('파일 다운로드에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 로딩 상태 처리
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loader"></div> {/* 로딩 스피너 */}
        <p className="ml-4">데이터를 불러오는 중입니다...</p>
      </div>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <div className="flex flex-col items-center mt-20">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="bg-MainColor text-white px-4 py-2 rounded"
        >
          이전 페이지로 돌아가기
        </button>
      </div>
    );
  }

  // payment 데이터가 없을 경우 처리
  if (!payment) {
    return (
      <div className="flex flex-col items-center mt-20">
        <p className="text-red-500 mb-4">상세 정보를 불러올 수 없습니다.</p>
        <button
          onClick={() => navigate(-1)}
          className="bg-MainColor text-white px-4 py-2 rounded"
        >
          이전 페이지로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <section className="max-w-[1200px] w-full mx-auto mt-[130px] mb-[60px]">
      {/* 뒤로가기 */}
      <div className="bg-white w-[40px] h-[40px] border border-gray-200 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center">
        <IoIosArrowBack
          onClick={() => navigate(-1)}
          className="text-[#4d6786] cursor-pointer"
          size={20}
        />
      </div>
      {/* 추가 내용: 사용자 프로필 정보 */}
      {payment.product && payment.product.nickname && payment.product.profileImageUrl && (
        <div className="py-4 mt-10">
          <h3 className="text-lg font-bold mb-4">판매자 정보</h3>
          <div className="flex items-center bg-white p-4 rounded-lg shadow-md">
            <img
              src={`${process.env.REACT_APP_API_BASE_URL}${payment.product.profileImageUrl}`}
              alt={`${payment.product.nickname}의 프로필`}
              className="w-16 h-16 rounded-full object-cover mr-4"
            />
            <div>
              <p className="font-semibold">{payment.product.nickname}</p>
              {/* 추가적인 사용자 정보가 있다면 여기에 표시 */}
            </div>
          </div>
        </div>
      )}
      <div className="mt-12">
        <h3 className="text-lg flex gap-3 font-bold mb-2">
          {payment.approvedAt
            ? new Date(payment.approvedAt).toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
            : '날짜 정보 없음'}{' '}
          <span className="text-[#999] font-normal">주문번호 {payment.orderId}</span>
        </h3>
        <div className="border p-4 mb-4 rounded-lg">
          <div
            className="flex items-start justify-between cursor-pointer"
            onClick={() => setIsDetailVisible(!isDetailVisible)} // 클릭 시 상세 정보 토글
          >
            {/* 상품 정보 */}
            <div className="flex items-start">
              {payment.product ? (
                <>
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}${payment.product.image}`}
                    alt={payment.product.name}
                    className="w-20 h-20 object-cover mr-4"
                  />
                  <div>
                    <p className="font-semibold mb-1 text-base flex">
                      {payment.product.name}
                      {/* <span className="ml-[10px] text-[12px]">주문번호 {payment.orderId}</span> */}
                    </p>
                    <p className="text-gray-500 mb-1">
                      결제 수단 {payment.easyPayProvider}
                    </p>
                    <p className="text-gray-500">
                      결제 금액 {formatNumber(payment.totalAmount || 0)} 원
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-red-500">상품 정보가 없습니다.</p>
              )}
            </div>

            {/* 버튼 */}
            <div className="flex flex-col space-y-2 w-[160px]">
              {payment.product && (
                <button
                  onClick={handleDownload}
                  className="bg-MainColor justify-center hover:bg-opacity-80 transition duration-300 flex items-center gap-2 text-white px-4 py-2 rounded"
                >
                  <FaDownload />
                  <span className="text-sm font-medium tracking-wide">
                    다운로드
                  </span>
                </button>
              )}
              <button
                className={`bg-gray-200 text-gray-600 px-4 py-2 rounded ${
                  !payment.product ? 'cursor-not-allowed opacity-50' : ''
                }`}
                onClick={() => {
                  if (payment.product) {
                    navigate(`/purchase/details/${payment.id}?date=${encodeURIComponent(payment.requestedAt || '')}`, { state: { payment } });
                  } else {
                    alert('상세 정보를 불러올 수 없습니다.');
                  }
                }}
                disabled={!payment.product} // Disable button if product is null
              >
                자세히보기
              </button>
            </div>
          </div>

          {/* 클릭 시 상세 정보 표시 */}
          {isDetailVisible && payment.product && (
            <div className="py-4 mt-4">
              <h3 className="text-lg font-bold mb-4">상품 상세 정보</h3>
              <div className="flex flex-col bg-white p-4 rounded-lg ">
                {/* 상품 설명 */}
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">상품 설명</h4>
                  <div
                    className="text-gray-700"
                    dangerouslySetInnerHTML={{ __html: payment.product.description || '설명이 없습니다.' }}
                  />
                </div>

                {/* 사용 안내 */}
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">사용 안내</h4>
                  <div
                    className="text-gray-700"
                    dangerouslySetInnerHTML={{ __html: payment.product.usageInstructions || '사용 안내가 없습니다.' }}
                  />
                </div>

                {/* 소개 */}
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">소개</h4>
                  <div
                    className="text-gray-700"
                    dangerouslySetInnerHTML={{ __html: payment.product.introduction || '소개가 없습니다.' }}
                  />
                </div>

                {/* 상품 이미지 갤러리 */}
                {payment.product.productImages && payment.product.productImages.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">상품 이미지</h4>
                    <div className="flex flex-wrap gap-4">
                      {payment.product.productImages.map((image) => (
                        <img
                          key={image.id}
                          src={`${process.env.REACT_APP_API_BASE_URL}${image.imageUrl}`}
                          alt={`상품 이미지 ${image.id}`}
                          className="w-32 h-32 object-cover rounded"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* 리뷰 및 평점 */}
                <div className="mb-4 mt-4">
                  <h4 className="font-semibold mb-2">리뷰 및 평점</h4>
                  <div className="flex items-center">
                    <span className="text-yellow-500 mr-2">
                      {'★'.repeat(payment.product.star)}{'☆'.repeat(5 - payment.product.star)}
                    </span>
                    <span>{payment.product.review}개의 리뷰</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 결제 정보 */}
      <div className="py-4">
        <h3 className="text-lg font-bold mb-4">결제 정보</h3>

        {/* 결제 수단과 금액 정보 */}
        <div className="mb-4">

          {/* 결제 금액 정보는 product가 존재할 때만 렌더링 */}
          {payment.product ? (
            <div className="flex flex-col bg-gray-100 p-4 rounded mt-4">
  
              <div className="flex mb-4 ">
                <span className="font-semibold text-MainTextColor text-base w-[120px]">
                결제수단
                </span>
                <span>{payment.easyPayProvider || '정보 없음'}</span>
              </div>
              <div className="flex mb-4 ">
                <span className="font-semibold text-MainTextColor text-base w-[120px]">
                결제형식
                </span>
                <span>{payment.method || '정보 없음'}</span>
              </div>
              <div className="flex mb-4 ">
                <span className="font-semibold text-MainTextColor text-base w-[120px]">
                결제 시간
                </span>
                <span>{payment.requestedAt || '정보 없음'}</span>
              </div>
              <div className="flex mb-4 ">
                <span className="font-semibold text-MainTextColor text-base w-[120px]">
                  상품 가격
                </span>
                <span>{formatNumber(payment.product.originalPrice)}</span>
              </div>
              <div className="flex mb-4 ">
                <span className="font-semibold text-MainTextColor text-base w-[120px]">
                  할인 가격
                </span>
                <span>{formatNumber(payment.product.originalPrice-payment.totalAmount)}</span>
              </div>
              <div className="border-t-2 border-dashed border-[#bfbfbf] w-full pt-4 flex">
                <span className="font-semibold tracking-wide text-[#ff2853] text-base w-[120px]">
                  총 결제금액
                </span>
                <span className="font-semibold tracking-wide text-[#ff2853] text-base">
                  {formatNumber(payment.totalAmount)}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-red-500">상품 정보가 없어 결제 금액을 표시할 수 없습니다.</p>
          )}
        </div>
      </div>


    </section>
  );
}
